import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { publicApi } from '@/lib/api';

const CHECK_INTERVAL = 60000; // Check every 60s

export function useVersionCheck() {
  const lastVersion = useRef(null);
  const updatePending = useRef(false);
  const location = useLocation();

  // 1. Intervalle de vérification de version
  useEffect(() => {
    const check = async () => {
      try {
        const res = await publicApi.getVersion();
        const version = res.data?.version;
        if (!version) return;
        
        if (lastVersion.current === null) {
          lastVersion.current = version;
        } else if (lastVersion.current !== version) {
          updatePending.current = true;
          attemptSilentReload();
        }
      } catch (e) {
        // Échec silencieux
      }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Détermine si l'utilisateur est en cours de saisie/modification de formulaire
  const isUserEditing = () => {
    const active = document.activeElement;
    if (!active) return false;
    const tag = active.tagName.toLowerCase();
    
    // Si un input ou textarea a le focus, ou si le document a du contenu modifiable
    if (tag === 'input' || tag === 'textarea' || active.isContentEditable) {
      return true;
    }
    return false;
  };

  const attemptSilentReload = () => {
    if (!updatePending.current) return;
    if (isUserEditing()) {
      // Reporter le rechargement car l'utilisateur est en cours de saisie
      return;
    }
    // Rechargement silencieux pour rafraîchir le cache
    window.location.reload();
  };

  // 2. Tenter le rechargement lors de la navigation (changement de page/route)
  useEffect(() => {
    if (updatePending.current) {
      attemptSilentReload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // 3. Tenter le rechargement lorsque la fenêtre récupère le focus (l'utilisateur revient sur l'onglet)
  useEffect(() => {
    const handleFocus = () => {
      if (updatePending.current) {
        attemptSilentReload();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // N'expose plus aucun état visuel pour la bannière UI
  return { hasUpdate: false, refresh: () => {}, dismiss: () => {} };
}
